import type {
  ProjectsInArtworkListItem,
  ProjectsInArtworkVendorDetail,
} from "@/services/artworkService";
import {
  ARTWORK_LIST_ROW_KIND_PRODUCT,
  ARTWORK_LIST_ROW_KIND_VERSION,
} from "@/components/ProjectsInArtwork/utils/artworkListApiTransform";
import { vendorDetailsHasNestedVersionRows } from "@/components/ProjectsInArtwork/utils/artworkHelpers";

/** Match Active Projects list / product-info display normalization for `vendorAssessmentStatus`. */
function toDisplayStatusForActiveProjectsRow(raw: unknown): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (s.includes(" ") && !s.includes("_")) return s;
  return s
    .split(/_/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Converts Projects in Artwork **list** `vendorDetails` (flat per-assessment rows) into
 * `vendorDetails[].vendorList[]` groups so {@link enumerateVendorAssessmentRows} / Active Projects
 * URL → vendor tab sync works before `getProductInfoDetails` returns.
 */
export function groupFlatArtworkVendorDetailsForActiveProjectsUrl(
  vendorDetails: unknown
): Record<string, unknown>[] {
  if (!Array.isArray(vendorDetails) || vendorDetails.length === 0) return [];

  type Acc = {
    vendorId: string;
    vendorCorporateName: string;
    goldNumber: string;
    versionRows: Record<string, unknown>[];
  };

  const groups: Acc[] = [];
  const indexByVendorKey = new Map<string, number>();

  for (const item of vendorDetails) {
    if (item == null || typeof item !== "object") continue;
    const v = item as Record<string, unknown>;

    const aidRaw = v.vendorAssessmentId ?? v.assessmentId;
    const aidStr = aidRaw != null && String(aidRaw).trim() !== "" ? String(aidRaw).trim() : "";
    if (!aidStr) continue;

    const vendorIdRaw = v.vendorId;
    let vendorIdStr =
      vendorIdRaw != null && String(vendorIdRaw).trim() !== "" ? String(vendorIdRaw).trim() : "";
    if (!vendorIdStr) {
      const idRaw = v.id;
      if (idRaw != null && String(idRaw).trim() !== "") {
        vendorIdStr = String(idRaw).trim();
      }
    }
    const groupKey = vendorIdStr || aidStr;

    const corp = String(v.vendorCorporateName ?? v.vendorName ?? "").trim();
    const goldNumber = v.goldNumber != null ? String(v.goldNumber) : "";

    const stRaw =
      v.businessStatus ??
      (v as { vendorAssessmentStatus?: unknown }).vendorAssessmentStatus ??
      v.artworkStatus ??
      v.status ??
      "";
    const workflowStatus = Array.isArray(v.workflowStatus)
      ? (v.workflowStatus as unknown[]).map((x) => String(x))
      : [];

    const versionName = String(v.versionName ?? "").trim();

    const versionRow: Record<string, unknown> = {
      assessmentId: aidStr,
      vendorAssessmentId: aidStr,
      vendorAssessmentStatus: toDisplayStatusForActiveProjectsRow(stRaw),
      businessStatus: String(stRaw ?? ""),
      workflowStatus,
      artworkDeadline: String(v.artWorkDeadline ?? v.artworkDeadline ?? "").trim(),
      ...(versionName ? { versionName } : {}),
    };

    let gIdx = indexByVendorKey.get(groupKey);
    if (gIdx === undefined) {
      gIdx = groups.length;
      indexByVendorKey.set(groupKey, gIdx);
      groups.push({
        vendorId: vendorIdStr || groupKey,
        vendorCorporateName: corp || `Vendor ${aidStr}`,
        goldNumber,
        versionRows: [versionRow],
      });
    } else {
      const g = groups[gIdx];
      if (g) {
        g.versionRows.push(versionRow);
        if (!g.vendorCorporateName && corp) {
          g.vendorCorporateName = corp;
        }
        if (!g.goldNumber && goldNumber) {
          g.goldNumber = goldNumber;
        }
      }
    }
  }

  return groups.map((g) => ({
    vendorId: g.vendorId,
    id: g.vendorId,
    vendorCorporateName: g.vendorCorporateName,
    goldNumber: g.goldNumber,
    vendorList: g.versionRows,
  }));
}

/** Extra fields when a list row is expanded into parent + version rows in the grid. */
export type ProjectsInArtworkGridRow = ProjectsInArtworkListItem & {
  __rowKind?: typeof ARTWORK_LIST_ROW_KIND_PRODUCT | typeof ARTWORK_LIST_ROW_KIND_VERSION;
  /** Real product id (stable even when `id` is a composite grid row id). */
  __gridProductId?: string;
  __versionDetail?: ProjectsInArtworkVendorDetail;
  /** 1-based version index within vendor group (expanded accordion rows), for `V{n}` when `versionName` is empty. */
  __versionRowOrdinal?: number;
  /** Version row: draw a separator below when the next version row is a different vendor. */
  __vendorGroupEnd?: boolean;
  /** First version sub-row of a product when it is not the first product in the list (top border between products). */
  __productGroupStart?: boolean;
};

export function toProjectsInArtworkListItemFromGridRow(row: ProjectsInArtworkGridRow): ProjectsInArtworkListItem {
  const realId = String(row.__gridProductId ?? row.id ?? "").trim();
  const { __rowKind: _rk, __gridProductId: _gp, __versionDetail: _vd, __versionRowOrdinal: _vo, __vendorGroupEnd: _vge, __productGroupStart: _pgs, ...rest } = row;
  return { ...(rest as object), id: realId } as ProjectsInArtworkListItem;
}

export function buildArtworkDetailHref(
  productId: string | number,
  vendorId?: string | number | null,
  vendorAssessmentId?: string | number | null
): string {
  const base = `/design-artwork/projects-in-artwork/${encodeURIComponent(String(productId))}`;
  const q = new URLSearchParams();
  const vid = vendorId != null && String(vendorId).trim() !== "" ? String(vendorId).trim() : "";
  const aid =
    vendorAssessmentId != null && String(vendorAssessmentId).trim() !== ""
      ? String(vendorAssessmentId).trim()
      : "";
  if (vid) q.set("vendorId", vid);
  if (aid) q.set("vendorAssessmentId", aid);
  const qs = q.toString();
  return qs ? `${base}?${qs}` : base;
}

/** Row payload for list → detail: full product + optional version row from `vendorDetails`. */
export function buildSelectedActiveProjectForArtworkNavigation(
  productRow: ProjectsInArtworkListItem,
  versionRow?: Record<string, unknown> | null
): Record<string, unknown> {
  const base = productRow as unknown as Record<string, unknown>;
  const rawDetails = base.vendorDetails;
  const vendorDetails = vendorDetailsHasNestedVersionRows(rawDetails)
    ? rawDetails
    : groupFlatArtworkVendorDetailsForActiveProjectsUrl(rawDetails);

  return {
    ...base,
    vendorDetails,
    name: base?.title ?? "",
    category: base?.productSpecificationType ?? "",
    cm: base?.categoryManagerUser ?? null,
    pc: base?.productCreator ?? null,
    ...(versionRow && typeof versionRow === "object" ? { __navigationVersion: versionRow } : {}),
  };
}
